/**
 * Holds the set of Progress Note headings currently mounted, so
 * `<TableOfContents>` can list only the anchors that exist. See `tocRegistry`.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { TocActionsContext, TocPresenceContext, type TocActions } from './tocRegistry'

export function TocPresenceProvider({ children }: { children: ReactNode }) {
  const [present, setPresent] = useState<ReadonlySet<string>>(() => new Set())

  const register = useCallback((id: string) => {
    setPresent((current) => {
      if (current.has(id)) return current
      const next = new Set(current)
      next.add(id)
      return next
    })
  }, [])

  const unregister = useCallback((id: string) => {
    setPresent((current) => {
      if (!current.has(id)) return current
      const next = new Set(current)
      next.delete(id)
      return next
    })
  }, [])

  const actions = useMemo<TocActions>(() => ({ register, unregister }), [register, unregister])

  return (
    <TocActionsContext.Provider value={actions}>
      <TocPresenceContext.Provider value={present}>{children}</TocPresenceContext.Provider>
    </TocActionsContext.Provider>
  )
}
